import React from 'react';
import { GreyBoxLoader, IconFont } from '@xarvio/elements';
import { Panel, type PanelSize, Separator } from '@xarvio/layout';
import { TranslateMessage } from '@xarvio/utils';
import { ColorCx, Text } from '@xarvio/corporate-styles';
import { Submenu } from '../../../../components/shared/Submenu/Submenu';
import { SubmenuItemLoading } from '../../../../components/shared/Submenu/SubmenuItemLoading';
import styles from '../StatusFieldsPanel/StatusFieldsPanel.module.scss';
import { useMeasure } from '../../../../hooks/useMeasure/useMeasure';
import { getListPanelLoadingRowCount } from './utils';

interface NewStatusPageSidePanelLoadingProps {
  withTitle?: boolean;
}

const NewStatusPageSidePanelLoading: React.FC<
  React.PropsWithChildren<NewStatusPageSidePanelLoadingProps>
> = ({ withTitle = true }) => {
  return (
    <Submenu
      titleLabelEl={
        withTitle ? (
          <Text type={'h3'} color={ColorCx.BLACK}>
            <TranslateMessage id="component.StatusPageSidePanel.title" />
          </Text>
        ) : null
      }
      titleIconEl={
        <IconFont name={'status-filled'} size={24} color={ColorCx.BLACK} />
      }
      isCollapsible={false}
    >
      <SubmenuItemLoading width={'80%'} />
      <SubmenuItemLoading width={'70%'} />
      <Separator light medium margin={'medium'} />
      <SubmenuItemLoading width={'80%'} />
      <SubmenuItemLoading width={'70%'} />
      <SubmenuItemLoading width={'80%'} />
    </Submenu>
  );
};

interface NewStatusPageListPanelLoadingProps {
  size: PanelSize;
}

const NewStatusPageListPanelLoading: React.FC<
  React.PropsWithChildren<NewStatusPageListPanelLoadingProps>
> = ({ size }) => {
  const { bounds, ref } = useMeasure<HTMLDivElement>();
  const rowCount = getListPanelLoadingRowCount(bounds.height);

  return (
    <Panel
      size={size}
      active
      className={size === 's' ? styles.panel : undefined}
    >
      <div className={styles.listPanelLoading}>
        <div className={styles.listPanelLoadingHeader}>
          <GreyBoxLoader className={styles.listPanelLoadingTitleIcon} />
          <div className={styles.listPanelLoadingTitleText}>
            <GreyBoxLoader className={styles.listPanelLoadingTitle} />
            <GreyBoxLoader className={styles.listPanelLoadingSubtitle} />
          </div>
          <GreyBoxLoader className={styles.listPanelLoadingClose} />
        </div>

        <div className={styles.listPanelLoadingActions}>
          <GreyBoxLoader className={styles.listPanelLoadingActionSmall} />
          <GreyBoxLoader className={styles.listPanelLoadingActionSmall} />
          <GreyBoxLoader className={styles.listPanelLoadingActionLarge} />
          <GreyBoxLoader className={styles.listPanelLoadingActionIcon} />
        </div>

        <div className={styles.listPanelLoadingFilters}>
          <GreyBoxLoader className={styles.listPanelLoadingFilter} />
          <GreyBoxLoader className={styles.listPanelLoadingFilterWide} />
          <GreyBoxLoader className={styles.listPanelLoadingFilter} />
        </div>

        <div className={styles.listPanelLoadingSearch}>
          <GreyBoxLoader className={styles.listPanelLoadingSearchInput} />
        </div>

        <div className={styles.listPanelLoadingGroup}>
          <div className={styles.listPanelLoadingCheckboxCell}>
            <GreyBoxLoader className={styles.listPanelLoadingCheckbox} />
          </div>
          <GreyBoxLoader className={styles.listPanelLoadingCropIcon} />
          <GreyBoxLoader className={styles.listPanelLoadingCropLabel} />
        </div>

        <div ref={ref} className={styles.listPanelLoadingRows}>
          {Array.from({ length: rowCount }, (_, index) => (
            <div className={styles.listPanelLoadingRow} key={index}>
              <div className={styles.listPanelLoadingCheckboxCell}>
                <GreyBoxLoader className={styles.listPanelLoadingCheckbox} />
              </div>
              <GreyBoxLoader className={styles.listPanelLoadingFieldShape} />
              <div className={styles.listPanelLoadingFieldText}>
                <GreyBoxLoader className={styles.listPanelLoadingFieldName} />
                <GreyBoxLoader
                  className={styles.listPanelLoadingFieldDetails}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
};

export { NewStatusPageListPanelLoading, NewStatusPageSidePanelLoading };
